import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { when } from 'lit/directives/when.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';

type ToggleVariant = 'default' | 'slim';
type ToggleSize = 'sm' | 'md';

/**
 * A binary switch control that toggles between on and off states with immediate effect.
 *
 * Available in two visual variants (Default = padded pill thumb; Slim = edge-to-edge thumb
 * with an inset 1 px border ring on the track) and two sizes (sm, md). Optionally renders
 * an inline label and helper text via named slots; when the label slot is empty, the
 * consumer must supply `aria-label` on the host.
 *
 * @element asc-toggle
 *
 * @fires asc-change - Fired when the toggle state changes. Detail: `{ checked: boolean }`
 *
 * @slot label       - Optional label text displayed inline alongside the toggle. Provides the accessible name via native `<label for>` association when present.
 * @slot helper-text - Optional supporting text displayed beneath the label.
 *
 * @csspart root         - Outer flex row wrapper containing the button and label group.
 * @csspart base         - The `<button>` track — top-level style hook for dimensions, background, and border-radius.
 * @csspart thumb        - The sliding knob — override size, background, shadow, and border.
 * @csspart border       - Slim variant only — the inset 1 px track border ring.
 * @csspart label-group  - Column wrapper for label and helper-text — override gap and alignment.
 * @csspart label        - The `<label>` element — override typography and color.
 * @csspart helper-text  - The `<p>` helper text element — override typography and color.
 *
 * @cssprop --asc-toggle-track-bg                    - Track background, unchecked. Default: `var(--asc-bg-tertiary)`
 * @cssprop --asc-toggle-track-bg-checked             - Track background, checked. Default: `var(--asc-color-peak-green-500)`
 * @cssprop --asc-toggle-track-bg-checked-hover       - Track background, checked + hover. Default: `var(--asc-bg-brand-solid)`
 * @cssprop --asc-toggle-track-bg-disabled            - Track background, disabled. Default: `var(--asc-bg-disabled)`
 * @cssprop --asc-toggle-track-border                 - Slim track ring color, unchecked. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-toggle-track-border-disabled        - Slim track ring color, disabled. Default: `var(--asc-border-disabled_subtle)`
 * @cssprop --asc-toggle-thumb-bg                     - Thumb fill. Default: `var(--asc-fg-white)`
 * @cssprop --asc-toggle-thumb-bg-disabled            - Thumb fill, disabled. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-toggle-thumb-shadow                 - Thumb drop shadow color. Default: `var(--asc-shadow-xs)`
 * @cssprop --asc-toggle-thumb-border                 - Slim thumb border color, unchecked. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-toggle-thumb-border-checked         - Slim thumb border color, checked. Default: `var(--asc-color-peak-green-500)`
 * @cssprop --asc-toggle-thumb-border-checked-hover   - Slim thumb border color, checked + hover. Default: `var(--asc-bg-brand-solid)`
 * @cssprop --asc-toggle-track-width-sm               - Track width, Default variant, sm. Default: `36px`
 * @cssprop --asc-toggle-track-height-sm              - Track height, Default variant, sm. Default: `20px`
 * @cssprop --asc-toggle-thumb-size-sm                - Thumb diameter, sm (both variants). Default: `16px`
 * @cssprop --asc-toggle-track-width-md               - Track width, Default variant, md. Default: `44px`
 * @cssprop --asc-toggle-track-height-md              - Track height, Default variant, md. Default: `24px`
 * @cssprop --asc-toggle-thumb-size-md                - Thumb diameter, md (both variants). Default: `20px`
 * @cssprop --asc-toggle-slim-track-width-sm          - Track width, Slim variant, sm. Default: `32px`
 * @cssprop --asc-toggle-slim-track-height-sm         - Track height, Slim variant, sm. Default: `16px`
 * @cssprop --asc-toggle-slim-track-width-md          - Track width, Slim variant, md. Default: `40px`
 * @cssprop --asc-toggle-slim-track-height-md         - Track height, Slim variant, md. Default: `20px`
 * @cssprop --asc-toggle-track-padding                - Default variant inset padding (all sides). Default: `var(--asc-spacing-xxs)`
 * @cssprop --asc-toggle-focus-ring-color             - Focus ring outer color. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-toggle-label-gap                    - Gap between the button and the label group. Default: `var(--asc-spacing-sm)`
 * @cssprop --asc-toggle-label-color                  - Label text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-toggle-label-font-weight            - Label font weight. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-toggle-label-font-size              - Label font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-toggle-helper-color                 - Helper text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-toggle-helper-font-weight           - Helper text font weight. Default: `var(--asc-font-weight-regular)`
 * @cssprop --asc-toggle-helper-font-size             - Helper text font size. Default: `var(--asc-font-size-text-sm)`
 */
@customElement('asc-toggle')
export class AscToggle extends LitElement {
  static formAssociated = true;

  static override styles = css`
    :host {
      display: inline-block;

      /* ── Private resolved vars — set per-state via host selectors ── */
      /* Thumb translation: 0 when unchecked, computed when checked.   */
      --_thumb-translate: 0px;
    }

    [hidden] {
      display: none !important;
    }

    /* ═══════════════════════════════════════════════════════════
       CHECKED — thumb translation per variant × size
       ═══════════════════════════════════════════════════════════ */

    /* Default sm: 36 - 16 - 2*padding(2px) = 16px */
    :host([checked]) {
      --_thumb-translate: calc(
        var(--asc-toggle-track-width-sm, 36px) -
        var(--asc-toggle-thumb-size-sm, 16px) -
        var(--asc-toggle-track-padding, var(--asc-spacing-xxs)) -
        var(--asc-toggle-track-padding, var(--asc-spacing-xxs))
      );
    }

    /* Default md: 44 - 20 - 2*2 = 20px */
    :host([checked][size='md']) {
      --_thumb-translate: calc(
        var(--asc-toggle-track-width-md, 44px) -
        var(--asc-toggle-thumb-size-md, 20px) -
        var(--asc-toggle-track-padding, var(--asc-spacing-xxs)) -
        var(--asc-toggle-track-padding, var(--asc-spacing-xxs))
      );
    }

    /* Slim sm: 32 - 16 = 16px (no padding) */
    :host([checked][variant='slim']) {
      --_thumb-translate: calc(
        var(--asc-toggle-slim-track-width-sm, 32px) -
        var(--asc-toggle-thumb-size-sm, 16px)
      );
    }

    /* Slim md: 40 - 20 = 20px (no padding) */
    :host([checked][variant='slim'][size='md']) {
      --_thumb-translate: calc(
        var(--asc-toggle-slim-track-width-md, 40px) -
        var(--asc-toggle-thumb-size-md, 20px)
      );
    }

    /* ═══════════════════════════════════════════════════════════
       ROOT WRAPPER
       ═══════════════════════════════════════════════════════════ */

    [part='root'] {
      display: inline-flex;
      flex-direction: row;
      align-items: flex-start;
      gap: var(--asc-toggle-label-gap, var(--asc-spacing-sm));
    }

    /* ═══════════════════════════════════════════════════════════
       TRACK (button)
       Base styles shared across all variants and sizes.
       ═══════════════════════════════════════════════════════════ */

    [part='base'] {
      /* Reset button defaults */
      appearance: none;
      border: none;
      margin: 0;
      cursor: pointer;

      /* Track layout */
      display: flex;
      flex-shrink: 0;
      align-items: center;
      position: relative;
      overflow: hidden;
      box-sizing: border-box;

      /* Default variant dimensions (sm) */
      width: var(--asc-toggle-track-width-sm, 36px);
      height: var(--asc-toggle-track-height-sm, 20px);
      padding: var(--asc-toggle-track-padding, var(--asc-spacing-xxs));

      /* Colors */
      background-color: var(--asc-toggle-track-bg, var(--asc-bg-tertiary));
      border-radius: var(--asc-radius-full);

      transition: background-color var(--asc-transition-fast);
    }

    /* ── Size: md (Default variant) ─────────────────────────── */
    :host([size='md']) [part='base'] {
      width: var(--asc-toggle-track-width-md, 44px);
      height: var(--asc-toggle-track-height-md, 24px);
    }

    /* ── Variant: Slim ──────────────────────────────────────── */
    :host([variant='slim']) [part='base'] {
      width: var(--asc-toggle-slim-track-width-sm, 32px);
      height: var(--asc-toggle-slim-track-height-sm, 16px);
      padding: 0;
    }

    :host([variant='slim'][size='md']) [part='base'] {
      width: var(--asc-toggle-slim-track-width-md, 40px);
      height: var(--asc-toggle-slim-track-height-md, 20px);
    }

    /* ── Checked track ──────────────────────────────────────── */
    :host([checked]) [part='base'] {
      background-color: var(--asc-toggle-track-bg-checked, var(--asc-color-peak-green-500));
    }

    :host([checked]:not([disabled])) [part='base']:hover {
      background-color: var(--asc-toggle-track-bg-checked-hover, var(--asc-bg-brand-solid));
    }

    /* ── Disabled track ─────────────────────────────────────── */
    :host([disabled]) [part='base'] {
      background-color: var(--asc-toggle-track-bg-disabled, var(--asc-bg-disabled));
      cursor: not-allowed;
    }

    /* ── Focus ring ─────────────────────────────────────────── */
    [part='base']:focus-visible {
      outline: none;
      box-shadow:
        0 0 0 2px white,
        0 0 0 4px var(--asc-toggle-focus-ring-color, var(--asc-focus-ring));
    }

    /* ═══════════════════════════════════════════════════════════
       SLIM BORDER RING
       Absolutely positioned 1 px inset ring — Slim variant only.
       ═══════════════════════════════════════════════════════════ */

    [part='border'] {
      position: absolute;
      inset: 0;
      border: var(--asc-border-width) solid
        var(--asc-toggle-track-border, var(--asc-border-secondary));
      border-radius: var(--asc-radius-full);
      pointer-events: none;
    }

    :host([checked]) [part='border'] {
      border-color: transparent;
    }

    :host([disabled]) [part='border'] {
      border-color: var(
        --asc-toggle-track-border-disabled,
        var(--asc-border-disabled_subtle)
      );
    }

    /* ═══════════════════════════════════════════════════════════
       THUMB
       ═══════════════════════════════════════════════════════════ */

    [part='thumb'] {
      flex-shrink: 0;
      box-sizing: border-box;
      border-radius: var(--asc-radius-full);

      /* Default sm dimensions */
      width: var(--asc-toggle-thumb-size-sm, 16px);
      height: var(--asc-toggle-thumb-size-sm, 16px);

      background-color: var(--asc-toggle-thumb-bg, var(--asc-fg-white));
      box-shadow: 0 1px 2px 0 var(--asc-toggle-thumb-shadow, var(--asc-shadow-xs));

      /* Smooth slide animation driven by --_thumb-translate */
      transform: translateX(var(--_thumb-translate));
      transition: transform var(--asc-transition-fast);
    }

    /* ── md thumb ───────────────────────────────────────────── */
    :host([size='md']) [part='thumb'] {
      width: var(--asc-toggle-thumb-size-md, 20px);
      height: var(--asc-toggle-thumb-size-md, 20px);
    }

    /* ── Slim thumb border ──────────────────────────────────── */
    :host([variant='slim']) [part='thumb'] {
      border: var(--asc-border-width) solid
        var(--asc-toggle-thumb-border, var(--asc-border-secondary));
    }

    :host([variant='slim'][checked]) [part='thumb'] {
      border-color: var(--asc-toggle-thumb-border-checked, var(--asc-color-peak-green-500));
    }

    :host([variant='slim'][checked]:not([disabled])) [part='base']:hover [part='thumb'] {
      border-color: var(
        --asc-toggle-thumb-border-checked-hover,
        var(--asc-bg-brand-solid)
      );
    }

    /* ── Disabled thumb ─────────────────────────────────────── */
    :host([disabled]) [part='thumb'] {
      background-color: var(--asc-toggle-thumb-bg-disabled, var(--asc-bg-disabled_subtle));
      box-shadow: none;
    }

    /* ═══════════════════════════════════════════════════════════
       LABEL GROUP
       ═══════════════════════════════════════════════════════════ */

    [part='label-group'] {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    [part='label'] {
      display: block;
      margin: 0;
      cursor: pointer;

      color: var(--asc-toggle-label-color, var(--asc-text-secondary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-toggle-label-font-size, var(--asc-font-size-text-sm));
      font-weight: var(--asc-toggle-label-font-weight, var(--asc-font-weight-medium));
      line-height: var(--asc-line-height-text-sm);
    }

    :host([disabled]) [part='label'] {
      cursor: not-allowed;
    }

    [part='helper-text'] {
      display: block;
      margin: 0;

      color: var(--asc-toggle-helper-color, var(--asc-text-tertiary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-toggle-helper-font-size, var(--asc-font-size-text-sm));
      font-weight: var(--asc-toggle-helper-font-weight, var(--asc-font-weight-regular));
      line-height: var(--asc-line-height-text-sm);
    }
  `;

  /** The visual style — Default: padded pill thumb; Slim: edge-to-edge thumb with track border ring. */
  @property({ type: String, reflect: true }) variant: ToggleVariant = 'default';

  /** The toggle size. */
  @property({ type: String, reflect: true }) size: ToggleSize = 'sm';

  /** Whether the toggle is in the on (checked) state. */
  @property({ type: Boolean, reflect: true }) checked = false;

  /** Prevents interaction and renders the disabled visual state. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Optional value reported when used inside a form. */
  @property({ type: String }) value = '';

  /** Optional field name when used inside a form. */
  @property({ type: String }) name = '';

  /**
   * Accessible label forwarded to the internal button when the label slot is empty.
   * Set `aria-label` on `<asc-toggle>` directly; it is automatically applied to the
   * focusable `<button role="switch">` inside the shadow root.
   *
   * Overrides the ARIA mixin's `ariaLabel` to make the property reactive in Lit
   * so that template re-renders are triggered when the value changes.
   */
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;

  /**
   * One or more element IDs whose text provides the accessible label for the button,
   * forwarded from the host when the label slot is empty.
   */
  @property({ attribute: 'aria-labelledby' }) ariaLabelledby: string | null = null;

  @state() private _hasLabel = false;
  @state() private _hasHelperText = false;

  private readonly _uid = generateUid();
  private readonly _internals: ElementInternals = this.attachInternals();

  private _defaultChecked: boolean | undefined;

  /** Returns the aria-label to apply to the button — only forwarded when the label slot is empty. */
  private get _buttonAriaLabel(): string | undefined {
    return !this._hasLabel ? (this.ariaLabel ?? undefined) : undefined;
  }

  /** Returns the aria-labelledby to apply to the button — only forwarded when the label slot is empty. */
  private get _buttonAriaLabelledby(): string | undefined {
    return !this._hasLabel ? (this.ariaLabelledby ?? undefined) : undefined;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Remove forwarded ARIA attributes from the host element. Their values are already
    // captured as reactive properties and forwarded to the inner <button role="switch">.
    // Leaving them on the host (which has no role) causes axe to flag aria-prohibited-attr.
    this.removeAttribute('aria-label');
    this.removeAttribute('aria-labelledby');
    // Capture the initial HTML-declared checked state on first connection only,
    // so formResetCallback can restore it correctly on <form> reset.
    if (this._defaultChecked === undefined) {
      this._defaultChecked = this.checked;
    }
  }

  private _updateFormValue(): void {
    this._internals.setFormValue(this.checked ? (this.value || 'on') : null);
  }

  private _handleClick(): void {
    if (this.disabled) return;
    this.checked = !this.checked;
    dispatch<{ checked: boolean }>(this, 'asc-change', { detail: { checked: this.checked } });
  }

  private _handleLabelSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasLabel = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleHelperSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasHelperText = slot.assignedNodes({ flatten: true }).length > 0;
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('checked') || changed.has('value')) {
      this._updateFormValue();
    }
  }

  formResetCallback(): void {
    // Restores the initial HTML-declared checked state. Does not dispatch `asc-change`
    // because this is a programmatic reset, not a user interaction — `asc-change` is
    // only dispatched from `_handleClick` which is wired to the click event.
    this.checked = this._defaultChecked ?? false;
    this._updateFormValue();
  }

  override render() {
    const btnId = `${this._uid}-btn`;
    const helperId = `${this._uid}-helper`;

    return html`
      <div part="root">
        <button
          part="base"
          type="button"
          id=${btnId}
          role="switch"
          aria-checked=${this.checked ? 'true' : 'false'}
          aria-disabled=${this.disabled ? 'true' : 'false'}
          aria-label=${ifDefined(this._buttonAriaLabel)}
          aria-labelledby=${ifDefined(this._buttonAriaLabelledby)}
          aria-describedby=${ifDefined(this._hasHelperText ? helperId : undefined)}
          ?disabled=${this.disabled}
          @click=${this._handleClick}
        >
          ${when(
            this.variant === 'slim',
            () => html`<div part="border" aria-hidden="true"></div>`,
          )}
          <div part="thumb" aria-hidden="true"></div>
        </button>

        <div
          part="label-group"
          ?hidden=${!this._hasLabel && !this._hasHelperText}
        >
          <label
            part="label"
            for=${btnId}
            ?hidden=${!this._hasLabel}
          >
            <slot name="label" @slotchange=${this._handleLabelSlotChange}></slot>
          </label>
          <p
            part="helper-text"
            id=${helperId}
            ?hidden=${!this._hasHelperText}
          >
            <slot name="helper-text" @slotchange=${this._handleHelperSlotChange}></slot>
          </p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-toggle': AscToggle;
  }
}
